import { useEffect, useState, useCallback, useContext } from "react";
import { Box, Button, Container, MenuItem, Typography, useMediaQuery, Chip, TextField, DialogActions} from "@mui/material";
import { DataGrid, useGridApiRef} from "@mui/x-data-grid";
import { CheckCircleOutline, InfoOutline, Style, SystemUpdateAltOutlined, Verified} from "@mui/icons-material";
import GradientLoader from "../../components/CommanComponents/GradientLoader";
import DialogBox from "../../components/ajax/DialogBox";
import { useNavigate } from "react-router-dom";
import { callPostApi } from "../../function/ApiCallFunction";
import { toast } from "react-toastify";
import { DataContext } from "../../context/DataContext";

const GeneratedQR = () => 
{  
    const navigate = useNavigate();
    const {loginData }  = useContext(DataContext);
    const store_id      = loginData?.store_info?.store_id;

    const isMobile = useMediaQuery("(max-width:768px)");

    const apiRef = useGridApiRef();
    const [datar, setDatar] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadedNo, setLoadedNo] = useState("");

    const [amount, setAmount] = useState("");
    const [errors, setErrors] = useState({});
    const [open, setOpen] = useState(false);

    const [selectionModel, setSelectionModel] = useState([]);
    const [filterLimit, setFilterLimit] = useState(100);
    const [filterStatus, setFilterStatus] = useState("Pending");
    const [filterLotNo, setFilterLotNo] = useState("");
    const [filterLoadedNo, setFilterLoadedNo] = useState("");

    // QR List API Function
    const QRListApi = useCallback(async (status, lotno, loadedno, limit) => 
    {
      try 
      {
        setLoading(true);
        let qrstatus = "";

        switch (status) 
        {
          case "Pending": qrstatus = "0"; break;
          case "Activated": qrstatus = "2"; break;
          case "Loaded": qrstatus = "1"; break;
          default: qrstatus = "0";
        }

        const payload = {mod: "CASHI_QECODE_LIST", data_arr: { store_id, genLotNumber: lotno || "", loadingNumber: loadedno || "",
                    qrcodeStatus: qrstatus, start: "0", limit: limit || filterLimit, }, };
        const apiResult = await callPostApi("cashi-qrcode", payload);

        if (apiResult.status === "200" && Array.isArray(apiResult.data)) 
        {
            const formattedData = apiResult.data.map((item, index) => ({
              id: item.id,
              sr: index + 1,
              lotno: item.gen_lot_number || "NA",
              qr: item.qrcode_number || "NA",
              expdate: item.qrcode_status === "0" ? item.date_added 
                      : item.qrcode_status === "1" ? item.loading_date
                      : item.qrcode_status === "2" ? item.active_date 
                      : "NA",
              amount: item.amount || "0",
              loadedno: item.loading_number || "0",
              statuss: item.qrcode_status_name || "NA",
              status: item.qrcode_status === "0" ? "Pending"
                    : item.qrcode_status === "2" ? "Activated"
                    : item.qrcode_status === "1" ? "Loaded"
                    : "NA",
          }));
          setDatar(formattedData);
        } 
        else { setDatar([]); }
      } 
      catch (err) 
      {
        console.error('API Error:', err);
        setDatar([]);
      } 
      finally { setLoading(false); }

    }, [store_id, filterLimit]);

    // Load QR API Function
    const loadqrapi = async (selectedArray) => 
    {
        try 
        {
          setLoading(true);
          const payload = { mod: "LOAD_CASHI_QECODE", data_arr: { store_id , amount: amount, loading_number: loadedNo,tagLotId: selectedArray, }, };
          const apiResult = await callPostApi("cashi-qrcode", payload);
        
          if (apiResult.status === "200") 
          {
            toast.success(apiResult.data);
            setOpen(false);
            setLoadedNo("");
            setAmount("");
            setTimeout(() => { QRListApi(filterStatus, filterLotNo, filterLoadedNo, filterLimit); }, 100);
          } 
          else { toast.error(apiResult.status_message || "Operation failed"); }
        } 
        catch (err) 
        {
          console.error('Load QR Error:', err);
          toast.error("Failed to load QR codes");
        } 
        finally { setLoading(false); }
    };

    // Validation Function
    const validate = () => 
    {
        const newErrors = {};
        if (!amount || isNaN(amount) || Number(amount) <= 0) {
          newErrors.amount = "Enter a valid Amount";
        }
        if (!loadedNo || isNaN(loadedNo) || Number(loadedNo) <= 0) {
          newErrors.loadedNo = "Enter a valid Loading Number";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Clear Filters Function
    const clearFun = () => 
    {
        setFilterStatus("Pending");
        setFilterLimit(100);
        setFilterLotNo("");
        setFilterLoadedNo("");
        QRListApi("Pending", "", "", 100);
    };

    // Handle OK Button Click
    const handleOk = () => 
    {
        const selectedArray = Array.from(apiRef.current.getSelectedRows().keys());
        if (!validate()) return;
        loadqrapi(selectedArray);
    };
 
    const handleSelectionChange = (newSelection) => 
    {
      if (JSON.stringify(newSelection) !== JSON.stringify(selectionModel)) {  setSelectionModel(newSelection); }
    };

    useEffect(() => 
    {
      if (filterStatus && filterLimit) 
      {
        QRListApi(filterStatus, filterLotNo, filterLoadedNo, filterLimit);
      }
    }, [filterStatus, filterLotNo, filterLoadedNo, filterLimit,QRListApi]);

    // Column Definitions
    const columns = [
        { field: "sr", headerName: "Sr no.", flex: isMobile ? undefined : 0.5 },
        { field: "qr", headerName: "QR", flex: isMobile ? undefined : 1 },
        { field: "amount", headerName: "Amount", flex: isMobile ? undefined : 1 },
        {
          field: "status",  headerName: "Status", flex: isMobile ? undefined : 1,
          renderCell: (params) => {
            let color = "default";
            let icon = null;
            let border = "default";

            switch (params.value) {
              case "Activated":
                color = "#098665";
                border = "1px solid #098665";
                icon = <Verified sx={{ fontSize: 18 }} />;
                break;
              case "Pending":
                color = "#e9aa0aff";
                border = "1px solid #e9aa0aff";
                icon = <InfoOutline sx={{ fontSize: 18 }} />;
                break;
              case "Loaded":
                color = "#0a32d4ff";
                border = "1px solid #0a32d4ff";
                icon = <CheckCircleOutline sx={{ fontSize: 18 }} />;
                break;
              default:
                color = "default";
            }
            return (
              <Chip  icon={icon} label={params.value || "Unknown"} variant="outlined"
                color={color}
                sx={{  borderRadius: "18px", fontWeight: 500, border: { border }, color: { color }, }} />
              ); },},
        { field: "lotno", headerName: "Lot Number", flex: isMobile ? undefined : 1 },
        { field: "loadedno", headerName: "Loading Number", flex: isMobile ? undefined : 1 },
        { field: "expdate", headerName: "Date", flex: isMobile ? undefined : 1 },
    ];

  return (
    <Container sx={{ my: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "start", mb: 2 }}>
        <Button variant="contained" sx={{ backgroundColor: "#000000", color: "#ffffff" }} onClick={() => navigate(-1)} >
          Back
        </Button>
      </Box>

      <Typography variant="h6" gutterBottom
        sx={{ background: "linear-gradient(195deg, #49a3f1, #1A73E8)", p: 1, color: "#ffffff", borderRadius: "4px",
          mb: 3, display: "flex", alignItems: "center", }}>
         <Style sx={{ mr: 1 }} /> Generated QR
      </Typography>
 
      {/* Load Button */}
      <Box sx={{ display: "flex", justifyContent: "end" }}>
        <Button  variant="contained"
          sx={{  background: "linear-gradient(195deg, #49a3f1, #1A73E8)", color: "#ffffff", ml: 1, }}
          onClick={() => { const selectedArray = Array.from(apiRef.current.getSelectedRows().keys());
            if (selectedArray.length === 0) {
              toast.error("Please select at least one QR before loading.");
            } else {
              setOpen(true);
            } }} >
          <SystemUpdateAltOutlined sx={{ mx: 1, fontSize: "22px" }} /> Load
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, my: 2 }}>
        <TextField  select label="Filter Status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          sx={{ minWidth: 140 }}>
          <MenuItem value="Pending">Pending</MenuItem>
        </TextField>

        <TextField type="text" label="Lot Number" value={filterLotNo} onChange={(e) => setFilterLotNo(e.target.value)}
          sx={{ minWidth: 140 }} />

        <TextField type="text" label="Loading Number" value={filterLoadedNo} onChange={(e) => setFilterLoadedNo(e.target.value)}
          sx={{ minWidth: 140 }} />

        <TextField select label="Limit" value={filterLimit} onChange={(e) => setFilterLimit(e.target.value)} sx={{ minWidth: 140 }} >
          <MenuItem value={100}>100</MenuItem>
          <MenuItem value={300}>300</MenuItem>
          <MenuItem value={500}>500</MenuItem>
          <MenuItem value={2000}>All</MenuItem>
        </TextField>

        {/* Filter Buttons */}
        <Button variant="outlined" color="primary" sx={{ height: 56 }} 
          onClick={() => QRListApi(filterStatus, filterLotNo, filterLoadedNo, filterLimit)} >
          Apply Filter
        </Button>

        <Button variant="outlined" color="error" sx={{ height: 56 }} onClick={clearFun} > Clear Filter </Button>
      </Box>

      {/* DataGrid */}
      <Box sx={{ height: "auto", width: "100%", mb: 4 }}>
        <DataGrid  apiRef={apiRef}  rows={datar} columns={columns} checkboxSelection getRowHeight={() => "auto"} 
          initialState={{ pagination: { paginationModel: { pageSize: 10 },}, }} pageSizeOptions={[10, 25, 50]}
           onRowSelectionModelChange={handleSelectionChange} selectionModel={selectionModel}
          sx={{
            "& .MuiDataGrid-cell": {
              whiteSpace: "normal",
              wordBreak: "break-word",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f0f0f0",
              color: "#000",
              fontWeight: "bold",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: 14,
              fontWeight: 600,
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
          }}
        />
      </Box>

      {/* Load Dialog */}
      <DialogBox  open={open}  onClose={() => setOpen(false)} title="Load QR Code" >
        <TextField className="transparent-textfield" fullWidth  margin="normal"  label="Loading No." required
          value={loadedNo} onChange={(e) => { setLoadedNo(e.target.value); setErrors((prev) => ({ ...prev, loadedNo: undefined }));}}
          error={!!errors.loadedNo} helperText={errors.loadedNo}  type="number" InputProps={{ inputProps: { min: 1 } }} />

        <TextField className="transparent-textfield" fullWidth margin="normal" label="Enter Amount"  required value={amount}
          onChange={(e) => { setAmount(e.target.value); setErrors((prev) => ({ ...prev, amount: undefined })); }}
          error={!!errors.amount}  helperText={errors.amount} type="number"  InputProps={{ inputProps: { min: 1 } }} />

        <DialogActions> 
          <Button onClick={() => setOpen(false)} color="primary"> Cancel </Button>
          <Button onClick={handleOk} color="primary"  variant="contained"> Ok </Button>
        </DialogActions>
      </DialogBox>

      {loading && <GradientLoader text="Loading" />}
    </Container>
  );
};

export default GeneratedQR;
